import type { FC, PropsWithChildren } from 'react';
import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';

import {
	useBidsAndOffersActive,
	useBidsAndOffersDidntWin,
	useBidsAndOffersUnderOffer,
	usePurchasesCancelled,
	usePurchasesComplete,
} from 'Stores/AccountFiltersStore/AccountFiltersStore';
import { parseUrlQueryToAccountFilterSlugState } from 'Stores/AccountFiltersStore/AccountFiltersStore.helpers';
import { AccountCategory, AccountSlugs } from 'Types/account';
import { useIsMounted } from 'Utilities/hooks/useIsMounted';

import type { AccountStoreFilters } from './AccountFiltersStore.types';

export const AccountFiltersStoreUrlSynchronizer: FC<PropsWithChildren> = ({ children }) => {
	const router = useRouter();

	const isMounted = useIsMounted();

	const bidsAndOffersActiveStore = useBidsAndOffersActive();
	const bidsAndOffersDidntWinStore = useBidsAndOffersDidntWin();
	const bidsAndOffersUnderOfferStore = useBidsAndOffersUnderOffer();
	const purchasesCompleteStore = usePurchasesComplete();
	const purchasesCancelledStore = usePurchasesCancelled();

	const activeStore: AccountStoreFilters | null = useMemo(() => {
		const dictionary: {
			[category: string]: {
				[slug: string]: AccountStoreFilters;
			};
		} = {
			[AccountCategory.BIDS]: {
				[AccountSlugs.ACTIVE]: bidsAndOffersActiveStore,
				[AccountSlugs.DIDNT_WIN]: bidsAndOffersDidntWinStore,
				[AccountSlugs.UNDER_OFFER]: bidsAndOffersUnderOfferStore,
			},
			[AccountCategory.PURCHASES]: {
				[AccountSlugs.CANCELLED]: purchasesCancelledStore,
				[AccountSlugs.COMPLETE]: purchasesCompleteStore,
			},
		};

		const { params } = router.query || {};

		const [category, slug] = params || [];

		return dictionary[String(category)] && dictionary[String(category)][String(slug)]
			? dictionary[String(category)][String(slug)]
			: null;
	}, [
		router,
		bidsAndOffersActiveStore,
		bidsAndOffersDidntWinStore,
		bidsAndOffersUnderOfferStore,
		purchasesCancelledStore,
		purchasesCompleteStore,
	]);

	useEffect(() => {
		if (!activeStore) {
			return;
		}

		const handleRouteChange = () => {
			activeStore.setSlugState(parseUrlQueryToAccountFilterSlugState(router?.query, activeStore));
		};

		if (!isMounted) {
			handleRouteChange();
		}

		router.events.on('routeChangeComplete', handleRouteChange);

		// To be removed when removing the isCleanDeskInitiativeEnabled flag
		// eslint-disable-next-line consistent-return
		return () => {
			router.events.off('routeChangeComplete', handleRouteChange);
		};
	}, [router, activeStore, isMounted]);

	return <>{children}</>;
};

export default AccountFiltersStoreUrlSynchronizer;
